import * as React from 'react'
import { useEffect, useRef } from 'react'

import {
  Control,
  FieldErrors,
  UseFormRegister,
  useFieldArray,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Field, Flex } from '@chakra-ui/react'

import {
  FlexTable,
  FlexTableBody,
  FlexTableCell,
  FlexTableHeader,
  FlexTableRow,
  Input,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import { FaXmarkRegular } from '@enechain/ecloud-icons'

import CircularButton from '~/pages/Billing/components/CircularButton'

import { BillingEmailEditSchema } from './billingEmailEditSchema'

const MAX_EMAIL_ROWS = 11

type Props = {
  register: UseFormRegister<BillingEmailEditSchema>
  control: Control<BillingEmailEditSchema>
  errors: FieldErrors<BillingEmailEditSchema>
  index: number
  isGeneral: boolean
  onRemoveBillingEmail: () => void
}

const BillingEmailEditFormItem: React.FC<Props> = ({
  register,
  control,
  errors,
  index,
  isGeneral,
  onRemoveBillingEmail,
}) => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({
    name: `billingEmails.${index}.emails` as const,
    control,
  })

  // 初回レンダー時にメール行が0件なら空行を1つ補う。
  // 2回目以降、プロダクト個別ブロックでメール行が全て削除されたら、ブロックごと削除する。
  // (全体送付先ブロックは空でも残す)
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      if (fields.length === 0) {
        append({ value: '' })
      }
      isFirstRender.current = false
    } else if (!isGeneral && fields.length === 0) {
      onRemoveBillingEmail()
    }
  }, [fields.length, append, isGeneral, onRemoveBillingEmail])

  return (
    <Box width="80%">
      <FlexTable
        borderRadius="radiiBase"
        borderWidth={1}
        columnStyles={{
          email: {
            alignment: 'left',
          },
        }}
      >
        <FlexTableHeader style="single-line">
          <FlexTableRow>
            <FlexTableCell
              columnKey="email"
              contents={{ text: t('pages.billing.label.email') }}
            />
          </FlexTableRow>
        </FlexTableHeader>
        <FlexTableBody style="multi-line">
          {fields.map((field, emailIndex) => (
            <FlexTableRow key={field.id}>
              <FlexTableCell
                columnKey="email"
                cellWidth={{
                  type: 'fixed',
                  width: '90%',
                }}
              >
                <Field.Root
                  invalid={
                    !!errors.billingEmails?.[index]?.emails?.[emailIndex]?.value
                  }
                  alignItems="stretch"
                >
                  <Input
                    maxLength={256}
                    {...register(
                      `billingEmails.${index}.emails.${emailIndex}.value` as const,
                    )}
                  />
                  <Field.ErrorText>
                    {
                      errors.billingEmails?.[index]?.emails?.[emailIndex]?.value
                        ?.message
                    }
                  </Field.ErrorText>
                </Field.Root>
              </FlexTableCell>
              <FlexTableCell columnKey="actions">
                <CircularButton
                  icon={FaXmarkRegular}
                  onClick={() => remove(emailIndex)}
                />
              </FlexTableCell>
            </FlexTableRow>
          ))}
        </FlexTableBody>
      </FlexTable>
      {fields.length < MAX_EMAIL_ROWS && (
        <Flex justifyContent="center" marginTop="2">
          <TertiaryButton onClick={() => append({ value: '' })}>
            {t('pages.billing.action.add_email')}
          </TertiaryButton>
        </Flex>
      )}
    </Box>
  )
}

export default BillingEmailEditFormItem
